// Guarded copy of WF37's `Validate + Record Manual Observations` table (the app can't
// import a workflow) — static S-90 pins the parity. Same class as taskNotify.ts / S-78.
//
// Every value — vitals AND the four labs — is recorded in `measure_state` (whose canonical
// measure types already include hba1c / ldl_c / egfr / uacr). Deliberately NOT `lab_result`:
// that table belongs to the automatic lab import, and a hand-typed row would collide with a
// real imported result — product decision 2026-08-05.
//
// ⚠️ BLOOD PRESSURE IS DELIBERATELY ABSENT and must never be added. RFA Appendix C p55:
// "Blood pressure must be collected using a validated, upper arm cuff that supports
// timestamped, source-verifiable transmission. Manual entry of values is not permitted. Each
// submission to CMS must reflect an average of at least three readings." A typed BP is a
// non-compliant OAP measure however carefully it is transcribed. Weight is the explicit
// opposite (Appendix C p56: "may be reported by the patient using a home scale").
//
// `maxAgeDays` is the CMS CLINICAL VALIDITY WINDOW — the maximum age a measure may have when
// submitted (Model Payment Amounts & Performance Targets, p6): BP / weight / BMI 15 days, labs
// 1 year. Past it the measure is INVALID, and a missing valid measure is non-attainment for
// that beneficiary. HbA1c and LDL-C take the stricter 1-year window rather than the 2 years
// allowed for patients without diabetes/dyslipidemia — stricter only refuses an entry CMS
// would have accepted.
//
// The min/max ranges are TYPO GUARDS, not clinical thresholds — classification values live in
// .claude/rules/clinical-thresholds.md and must never be read from here. They are set to the
// plausible ADULT range so a slipped digit is caught while a real extreme still gets through:
// widened, a typo reaches a Medicare report (cp 144 was filed with LDL 10 mg/dL, which is not a
// physiological value); tightened, a legitimate reading cannot be reported at all, which is
// non-attainment for that beneficiary. ⚠️ Ranges tightened 2026-08-09 — pending clinician sign-off
// per .claude/rules/clinical-thresholds.md.

import { KG_TO_LB } from './patientHome';

export interface ManualObservationMeta {
  label: string;
  unit: string;
  loinc: string;
  measureType: string;
  min: number;
  max: number;
  step: number;
  /**
   * CMS clinical validity window in days — how old the measurement may be at submission.
   * `null` = no window, which is only correct for a value CMS never receives (height).
   */
  maxAgeDays: number | null;
  submitted: boolean;
}

// ===== S90-PINNED TABLE START
export const MANUAL_OBSERVATIONS: Record<string, ManualObservationMeta> = {
  weight: {
    label: 'Body weight', unit: 'kg', loinc: '29463-7', measureType: 'weight', min: 30, max: 300, step: 0.1, maxAgeDays: 15, submitted: true,
  },
  height: {
    label: 'Height', unit: 'm', loinc: '', measureType: 'height', min: 1.2, max: 2.2, step: 0.01, maxAgeDays: null, submitted: false,
  },
  waist: {
    label: 'Waist circumference', unit: 'cm', loinc: '8280-0', measureType: 'waist', min: 50, max: 200, step: 0.5, maxAgeDays: 15, submitted: true,
  },
  hba1c: {
    label: 'Hemoglobin A1c', unit: '%', loinc: '4548-4', measureType: 'hba1c', min: 4, max: 18, step: 0.1, maxAgeDays: 365, submitted: true,
  },
  egfr: {
    label: 'Glomerular filtration rate', unit: 'mL/min/{1.73_m2}', loinc: '98979-8', measureType: 'egfr', min: 2, max: 150, step: 1, maxAgeDays: 365, submitted: true,
  },
  uacr: {
    label: 'Urine albumin/creatinine ratio', unit: 'mg/g', loinc: '14959-1', measureType: 'uacr', min: 0.5, max: 20000, step: 0.1, maxAgeDays: 365, submitted: true,
  },
  ldl: {
    label: 'LDL cholesterol', unit: 'mg/dL', loinc: '18262-6', measureType: 'ldl_c', min: 15, max: 400, step: 1, maxAgeDays: 365, submitted: true,
  },
};

// ===== S90-PINNED TABLE END

// No paired inputs remain (BP was the only pair, and it is not hand-enterable). Kept as an
// empty map so the modal's row renderer needs no special case if a pair ever returns.
export const PAIRED_SLUG: Record<string, string> = {};

export interface ManualDraft {
  value: string;
  collectedAt: string;
  /** Offered once per session: a suggestion re-applied on every preview locks the operator out of
   *  their own override (spec 2026-08-28-cms-elation-lab-prefill §3.7). */
  suggestionSeen: boolean;
  /** What the suggestion proposed, so an edit can be detected and the provenance dropped. */
  suggestedValue: string;
  suggestedCollectedAt: string;
  /** Composite entry only (height): the raw boxes. A half-typed height must not become a
   *  submittable one, and the box under the cursor must never be re-derived from the scalar. */
  valueFt: string;
  valueIn: string;
  sourceClaimed: 'operator' | 'elation_lab_report';
  sourceRef: SuggestionRef;
  /** 'collected' | 'resulted' — the provenance line must not say "collected" about a result date. */
  dateSource: string;
  testName: string;
}

export type ManualDrafts = Record<string, Partial<ManualDraft>>;

export interface SuggestionRef {
  report_id?: string;
  accession?: string;
  loinc?: string;
}

/** One lab value WF37 read out of the Elation chart and is PROPOSING. Never auto-submitted. */
export interface LabSuggestion {
  slug: string;
  value: number;
  unit?: string;
  collected_at: string;
  date_source?: string;
  report_id?: string;
  accession?: string;
  loinc?: string;
  test_name?: string;
  is_abnormal?: boolean;
}

export interface ManualEntry {
  slug: string;
  value: number;
  collected_at?: string;
  source_claimed?: 'operator' | 'elation_lab_report';
  source_ref?: SuggestionRef;
}

// 2dp: a signed WF50 write carrying a long float can diverge from PHP's json_encode and
// break the RSA signature (n8n-workflows.md bug class #13).
const round2 = (n: number): number => Math.round(n * 100) / 100;

const isBlank = (s: string | undefined): boolean => !s || s.trim() === '';

export const todayIso = (): string => new Date().toISOString().slice(0, 10);

// hasOwn is load-bearing: a bare lookup of 'constructor' resolves through the prototype.
export const metaFor = (slug: string): ManualObservationMeta | null => (
  Object.hasOwn(MANUAL_OBSERVATIONS, slug) ? MANUAL_OBSERVATIONS[slug] ?? null : null
);

export const isManualSlug = (slug: string): boolean => metaFor(slug) !== null;

// ── Entry units: the care team reads and types US customary ─────────────────────────────────
//
// CMS still receives metric — the table above is unchanged and stays S-90-pinned against WF37.
// Only the OPERATOR's unit moves. The reason is a live incident (2026-08-27, uid 14969654): the
// form offered a stale 109 kg rendered as "(240.3 lb)", the coordinator knew the patient was
// ~125 lb, and typed 125.1 — into the kg field. A 2.2x error, made by a human because the entry
// unit was not the unit they think in. Two previews later they worked out it wanted kg.
//
// This deliberately reverses the older "never convert on input" rule below. That rule protected
// against a conversion BUG; what it left unprotected was the conversion the operator performs in
// their head on every entry. Mitigations that keep its intent:
//   - the metric table remains the single source of the guard rails (min/max/step/window),
//   - the draft stores exactly what was typed, so nothing round-trips and drifts,
//   - conversion happens in ONE place, at the payload boundary,
//   - height is typed as feet + inches, the shape the enrollment funnel already asks patients for.
const IN_PER_CM = 1 / 2.54;
const M_PER_IN = 0.0254;
const IN_PER_M = 1 / M_PER_IN;

interface EntryUnit {
  unit: string;
  fromMetric: (n: number) => number;
  toMetric: (n: number) => number;
  /** Decimals of the OPERATOR's unit — bounds and step are rounded to it, the wire never is. */
  entryDp: number;
  /** Set when the measure is typed as two boxes rather than one number. */
  composite?: 'ft-in';
}

const ENTRY_UNIT: Record<string, EntryUnit> = {
  weight: {
    unit: 'lb', fromMetric: (kg) => kg * KG_TO_LB, toMetric: (lb) => lb / KG_TO_LB, entryDp: 1,
  },
  waist: {
    unit: 'in', fromMetric: (cm) => cm * IN_PER_CM, toMetric: (inch) => inch / IN_PER_CM, entryDp: 1,
  },
  height: {
    unit: 'in', fromMetric: (m) => m * IN_PER_M, toMetric: (inch) => inch * M_PER_IN, entryDp: 0, composite: 'ft-in',
  },
};

/**
 * Does this measure have a US entry unit that differs from the CMS one?
 *
 * Use this rather than comparing unit strings: a slug that is not hand-enterable at all (bmi,
 * systolic_bp) has no table entry, so `entryUnitFor` returns '' and a string comparison reads it
 * as "converted" — which rendered BMI as "31.2 (31.2 kg/m2)".
 */
export const hasUsEntryUnit = (slug: string): boolean => Object.hasOwn(ENTRY_UNIT, slug);

/** What the operator types and reads for this measure — US customary where it differs from CMS. */
export const entryUnitFor = (slug: string): string => (
  ENTRY_UNIT[slug]?.unit ?? metaFor(slug)?.unit ?? ''
);

/** Entered value → the unit CMS receives. Identity for measures with no US convention. */
export const entryToMetric = (slug: string, value: number): number => (
  ENTRY_UNIT[slug] ? round2(ENTRY_UNIT[slug].toMetric(value)) : value
);

/** CMS value → the unit the operator reads. Identity where they are the same unit. */
export const metricToEntry = (slug: string, value: number): number => {
  const conv = ENTRY_UNIT[slug];
  if (!conv) return value;
  const p = 10 ** conv.entryDp;
  return Math.round(conv.fromMetric(value) * p) / p;
};

/** 'ft-in' when the operator types this measure as two boxes, else null. */
export const compositeEntryFor = (slug: string): 'ft-in' | null => ENTRY_UNIT[slug]?.composite ?? null;

/** Inches → the way a US care team says a height. */
const ftInLabel = (totalInches: number): string => {
  const ft = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches - ft * 12);
  // 11.6 in rounds to 12 — carry, or it renders as 5 ft 12 in.
  return inches === 12 ? `${ft + 1} ft 0 in` : `${ft} ft ${inches} in`;
};

const wholeOrNull = (raw: string | number | null | undefined): number | null => {
  const s = String(raw ?? '').trim();
  return /^\d+$/.test(s) ? Number(s) : null;
};

/**
 * The two boxes → the one scalar the draft stores (total inches), '' while either is unfilled.
 * BOTH are required: reading a blank inches box as 0 turns a lone `5` into a complete, in-range,
 * submittable 1.52 m — which WF37 then keeps and reuses for every future submission.
 */
export function totalInchesOf(ft: string | number | null | undefined, inch: string | number | null | undefined): string {
  const f = wholeOrNull(ft);
  const i = wholeOrNull(inch);
  if (f === null || i === null) return '';
  return String(f * 12 + i);
}

/** A composite the operator started and did not finish. It BLOCKS submit — see totalInchesOf. */
export function compositeIncomplete(drafts: ManualDrafts): boolean {
  return Object.entries(drafts || {}).some(([slug, draft]) => (
    compositeEntryFor(slug) !== null
    && isBlank(draft?.value)
    && !(isBlank(draft?.valueFt) && isBlank(draft?.valueIn))
  ));
}

/** Total inches → feet + inches, for the boxes' own bounds. Blank in, blank out. */
export function feetInchesOf(totalInches: number | string | null | undefined): { ft: string; inch: string } {
  const n = Number(totalInches);
  if (!Number.isFinite(n) || n <= 0) return { ft: '', inch: '' };
  const whole = Math.round(n);
  const ft = Math.floor(whole / 12);
  return { ft: String(ft), inch: String(whole - ft * 12) };
}

/** min/max/step restated in the entry unit, for the input's bounds and its error message. */
export function entryBounds(slug: string): { min: number; max: number; step: number } | null {
  const meta = metaFor(slug);
  if (!meta) return null;
  const conv = ENTRY_UNIT[slug];
  if (!conv) return { min: meta.min, max: meta.max, step: meta.step };
  const p = 10 ** conv.entryDp;
  return {
    // Inward rounding: a bound that rounds outward would admit a metric value the guard rejects.
    min: Math.ceil(conv.fromMetric(meta.min) * p) / p,
    max: Math.floor(conv.fromMetric(meta.max) * p) / p,
    step: Math.max(1 / p, Math.round(conv.fromMetric(meta.step) * p) / p),
  };
}

/** '' when the value is usable (blank = simply not entered), else the operator-facing reason. */
export function manualValueError(slug: string, raw: string | undefined): string {
  if (isBlank(raw)) return '';
  const meta = metaFor(slug);
  if (!meta) return 'Not a reportable measure';
  const num = Number(String(raw).trim());
  if (!Number.isFinite(num)) return 'Enter a number';
  // The guard is always the metric one (S-90 parity with WF37); only the wording is converted.
  const value = entryToMetric(slug, round2(num));
  if (value < meta.min || value > meta.max) {
    // meta is non-null here, so entryBounds cannot be — fall back to the metric bounds anyway
    // rather than render "undefined" in an operator-facing message.
    const b = entryBounds(slug) ?? { min: meta.min, max: meta.max };
    return compositeEntryFor(slug) === 'ft-in'
      ? `Must be between ${ftInLabel(b.min)} and ${ftInLabel(b.max)}`
      : `Must be between ${b.min} and ${b.max} ${entryUnitFor(slug)}`;
  }
  return '';
}

/** '' when the date is usable (blank = defaults to today), else the reason. */
export function manualDateError(slug: string, raw: string | undefined): string {
  if (isBlank(raw)) return '';
  const meta = metaFor(slug);
  if (!meta) return 'Not a reportable measure';
  const day = String(raw).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return 'Use a YYYY-MM-DD date';
  const t = Date.parse(`${day}T00:00:00Z`);
  // Round-trip, not just parse: V8 rolls an impossible day over (2026-02-31 → 2026-03-03).
  if (!Number.isFinite(t) || new Date(t).toISOString().slice(0, 10) !== day) return 'Not a real date';
  if (day > todayIso()) return 'Date cannot be in the future';
  // `null` = no window. Height does not expire and is never submitted, so a window would be a rule
  // CMS does not have — and 0 rejected EVERY date including today (now > today's midnight).
  if (meta.maxAgeDays != null && Date.now() - t > meta.maxAgeDays * 86400000) {
    return `CMS accepts this measure only within ${meta.maxAgeDays} days of submission`;
  }
  return '';
}

const draftEntries = (drafts: ManualDrafts): [string, Partial<ManualDraft>][] => Object
  .entries(drafts || {})
  .filter(([, draft]) => !isBlank(draft?.value));

/** slug → reason, for every entered draft whose value or date is unusable. */
export function manualDraftErrors(drafts: ManualDrafts): Record<string, string> {
  const errors: Record<string, string> = Object.fromEntries(
    draftEntries(drafts)
      .map(([slug, draft]): [string, string] => [
        slug,
        manualValueError(slug, draft.value) || manualDateError(slug, draft.collectedAt),
      ])
      .filter(([, error]) => error !== ''),
  );
  return errors;
}

/**
 * Only these may ever be prefilled from a chart. WF37 allow-lists twice server-side; this file is
 * a guarded copy whose whole premise is not trusting the other side, and the failure it prevents
 * is concrete: `weight`/`waist` are entered in lb/in, so a metric suggestion would land a 2.2x
 * error in a field labelled and bounded in the other unit — the incident at ENTRY_UNIT above.
 */
const SUGGESTABLE = new Set(['hba1c', 'ldl', 'egfr', 'uacr']);

/** Round a proposed value onto the slug's own step, whatever that step is. */
const roundToStep = (slug: string, value: number): number => {
  const step = metaFor(slug)?.step ?? 0.1;
  const inv = 1 / step;
  return Math.round(value * inv) / inv;
};

/**
 * Prefill drafts from WF37's Elation lab suggestions. Blank drafts only, once per slug per session.
 * Returns the same object when nothing changed, so it is safe to call on every preview response.
 */
export function applySuggestions(
  drafts: ManualDrafts,
  suggestions: LabSuggestion[] | null | undefined,
): ManualDrafts {
  if (!Array.isArray(suggestions) || suggestions.length === 0) return drafts;
  const slugOf = (s: LabSuggestion | undefined): string => String(s?.slug ?? '');
  const usable = suggestions.filter((s, i) => {
    const slug = slugOf(s);
    if (!SUGGESTABLE.has(slug) || !metaFor(slug)) return false;
    // Never overwrite what the operator put there — typed, or a suggestion they already judged.
    if (!isBlank(drafts[slug]?.value) || drafts[slug]?.suggestionSeen) return false;
    // A dateless value would be stamped with TODAY by the recorder while the provenance points at
    // a grid collected months earlier — the CMS validity window would be measured from the wrong
    // end. WF37 already refuses these; refuse them here too rather than depend on it.
    if (isBlank(String(s?.collected_at ?? ''))) return false;
    if (!Number.isFinite(Number(s?.value))) return false;
    return suggestions.findIndex((x) => slugOf(x) === slug) === i;
  });
  if (usable.length === 0) return drafts;

  return usable.reduce<ManualDrafts>((acc, s) => {
    const slug = String(s.slug);
    const collectedAt = String(s.collected_at ?? '').slice(0, 10);
    const proposed = String(roundToStep(slug, Number(s.value)));
    return {
      ...acc,
      [slug]: {
        ...(acc[slug] || {}),
        value: proposed,
        collectedAt,
        suggestionSeen: true,
        suggestedValue: proposed,
        suggestedCollectedAt: collectedAt,
        sourceClaimed: 'elation_lab_report',
        sourceRef: {
          report_id: String(s.report_id ?? ''),
          accession: String(s.accession ?? ''),
          loinc: String(s.loinc ?? ''),
        },
        dateSource: String(s.date_source ?? ''),
        testName: String(s.test_name ?? ''),
      },
    };
  }, { ...drafts });
}

/**
 * The Elation provenance of a draft, or null once the operator has changed it. Value AND date must
 * both still match: a `source_ref` pointing at a grid whose collection date disagrees with the
 * submitted one asserts a provenance the report itself contradicts.
 */
export function provenanceOf(draft: Partial<ManualDraft> | undefined): {
  sourceRef: SuggestionRef; collectedAt: string; dateSource: string; testName: string;
} | null {
  if (!draft || draft.sourceClaimed !== 'elation_lab_report') return null;
  const value = String(draft.value ?? '').trim();
  const collectedAt = String(draft.collectedAt ?? '').trim();
  if (value === '' || value !== String(draft.suggestedValue ?? '')) return null;
  // Both blanks would compare equal, and a claim on a dateless entry points at a grid whose
  // collection date the submitted value does not carry.
  if (collectedAt === '' || collectedAt !== String(draft.suggestedCollectedAt ?? '')) return null;
  if (isBlank(draft.sourceRef?.report_id)) return null;
  return {
    sourceRef: draft.sourceRef ?? {},
    collectedAt,
    dateSource: String(draft.dateSource ?? ''),
    testName: String(draft.testName ?? ''),
  };
}

/** The `manual_observations[]` payload for cms_reporting_requested; drops blank + invalid drafts. */
export function manualObservationsPayload(drafts: ManualDrafts): ManualEntry[] {
  return draftEntries(drafts)
    .filter(([slug, draft]) => !manualValueError(slug, draft.value) && !manualDateError(slug, draft.collectedAt))
    .map(([slug, draft]) => {
      // THE conversion point. Everything upstream is what the operator typed; everything
      // downstream is what CMS receives.
      const entry: ManualEntry = { slug, value: entryToMetric(slug, round2(Number(String(draft.value).trim()))) };
      if (!isBlank(draft.collectedAt)) entry.collected_at = String(draft.collectedAt).trim().slice(0, 10);
      // Provenance rides only while the value and date are still the proposed ones. WF37 treats it
      // as an operator ASSERTION either way and lets it change nothing but the audit row.
      const prov = provenanceOf(draft);
      if (prov) {
        entry.source_claimed = 'elation_lab_report';
        entry.source_ref = prov.sourceRef;
      }
      return entry;
    });
}

export const hasManualEntries = (drafts: ManualDrafts): boolean => draftEntries(drafts).length > 0;
